// ----------------------------------------------------------------------- react
import { useEffect, useState } from "react";

// ------------------------------------------------------------------- internos
import {
  hojasParaActualizar, clavesDeLaPlanilla, actualizarPlanilla, resumenActualizacion,
  reemplazosDeUbicacion, recordarUbicaciones, textosReemplazos,
} from "../../shared/pernocteBitren";
import { traerUbicacionYPernocte } from "../../services/ubicacionPernocte";
import { traerPlanilla, guardarPlanilla, guardarVersion, CONFLICTO } from "../../services/espacio";
import { hoyAr, horaAr } from "../../shared/fechaAr";

/* «Actualizar ubicación y pernocte» en su ONLY BITRENES.
 *
 * Lautaro Díaz (Satelital), 19/10: «MAÑANA 20-10 CUANDO LLEGUE A LA OFICINA
 * QUIERO APRETAR ESE BOTON Y QUE ME ACTUALIZE LA POSICION Y ME DIGA DONDE
 * PERNOCTO». Va arriba de todo en Torrecillas: es lo primero que hace a la
 * mañana, y no necesita soltar ningún archivo.
 *
 * Para cada renglón con un interno en TRA de ARRIBOS BITREN y SALIDAS
 * BITREN, FlotaBot dice dónde está el tractor y dónde paró a la noche, y se
 * escribe en UBICACIÓN ACTUALIZADA y en la columna «PERNOCTE dd-mm» de hoy.
 * Las reglas de qué se escribe y qué no se toca, en `shared/pernocteBitren`.
 *
 * Como «Agregar … a SALIDAS BITREN»: la planilla se vuelve a traer justo
 * antes de escribir (FlotaBot puede tardar unos segundos y lo que él escribió
 * mientras tanto no se pisa), se saca la copia al Historial y, sin ella, no
 * se escribe nada; se guarda con el candado de `actualizado_en`. Sin textos
 * de ayuda en la pantalla, como el resto de Torrecillas.
 *
 * Después del resumen, qué decía cada UBICACIÓN ACTUALIZADA que cambió: a la
 * vista lo que no había puesto el botón (lo escrito a mano), y plegado lo
 * que el botón pisa de sí mismo cada mañana. Para saber qué había puesto,
 * se acuerda de lo que escribió en cada planilla (`MEMORIA`, en este
 * navegador, como las custodias de Torrecillas). Sin memoria —otra
 * computadora, la primera vez— todo va a la vista: de más, nunca de menos.
 * Por qué no alcanza con mirar la forma del texto, en `shared/pernocteBitren`.
 */

const MEMORIA = "torrecillas.ubicaciones";

function leerMemoria(planilla) {
  try {
    return (JSON.parse(localStorage.getItem(MEMORIA) || "{}") || {})[planilla] || {};
  } catch {
    return {};
  }
}

function guardarMemoria(planilla, m) {
  try {
    const todas = JSON.parse(localStorage.getItem(MEMORIA) || "{}") || {};
    localStorage.setItem(MEMORIA, JSON.stringify({ ...todas, [planilla]: m }));
  } catch {
    /* sin almacenamiento: la próxima vez los reemplazos van todos a la vista */
  }
}

export default function UbicacionPernocte({ planillas = [], planillaId = "", onPlanilla = () => {} }) {
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState("");
  const [lineas, setLineas] = useState([]);
  const [detalle, setDetalle] = useState(null);
  const [reemplazos, setReemplazos] = useState(null);

  // Lo que se muestra es de una planilla: si se elige otra, se borra.
  useEffect(() => {
    setError("");
    setLineas([]);
    setDetalle(null);
    setReemplazos(null);
  }, [planillaId]);

  const actualizar = async () => {
    const enLa = planillaId;
    if (!enLa) return;
    setOcupado(true);
    setError("");
    setLineas([]);
    setDetalle(null);
    setReemplazos(null);
    // «No pude abrirla» y «no pude guardarla» no son lo mismo.
    const abrir = async () => {
      try {
        return await traerPlanilla(enLa);
      } catch {
        setError("No se pudo abrir la planilla. Probá de nuevo.");
        return null;
      }
    };
    try {
      const p = await abrir();
      if (!p) return;
      if (!hojasParaActualizar(p.datos).length) {
        setError("Esa planilla no tiene ARRIBOS BITREN ni SALIDAS BITREN con las columnas TRA y UBICACIÓN ACTUALIZADA.");
        return;
      }
      const claves = clavesDeLaPlanilla(p.datos);
      if (!claves.length) {
        setError("No hay ningún interno en TRA en ARRIBOS BITREN ni en SALIDAS BITREN.");
        return;
      }
      const fecha = hoyAr();
      const r = await traerUbicacionYPernocte(claves, fecha);
      if (!r.ok) {
        setError(r.mensaje || r.error || "No se pudo consultar.");
        return;
      }
      // Se vuelve a traer justo antes: lo que escribió recién no se pisa.
      const q = await abrir();
      if (!q) return;
      const { datos, resultados } = actualizarPlanilla(q.datos, r.unidades || {}, fecha);
      if (resultados.some((x) => x.cambio)) {
        // Primero la copia de cómo estaba, al Historial: sin ella no se escribe.
        try {
          await guardarVersion(q.id);
        } catch {
          setError("No se pudo actualizar. No se escribió nada; probá de nuevo.");
          return;
        }
        await guardarPlanilla(q.id, q.nombre, datos, q.actualizado_en);
        // Ya quedó escrito: qué decía antes, y qué puso el botón esta vez.
        const memoria = leerMemoria(q.id);
        setReemplazos(textosReemplazos(reemplazosDeUbicacion(resultados, memoria)));
        guardarMemoria(q.id, recordarUbicaciones(memoria, resultados));
      }
      const [primera, ...resto] = resumenActualizacion(resultados, fecha);
      setLineas([`${q.nombre}: ${primera}`, ...resto]);
      setDetalle(claves.map((clave) => ({ clave, ...((r.unidades || {})[clave] || {}) })));
    } catch (e) {
      setError(
        e?.codigo === CONFLICTO
          ? "La planilla cambió mientras tanto. Probá de nuevo."
          : "No se pudo guardar la planilla. Probá de nuevo.",
      );
    } finally {
      setOcupado(false);
    }
  };

  if (!planillas.length) return null;

  return (
    <section style={{ margin: "0 0 16px" }}>
      <h3 style={{ margin: "0 0 8px" }}>Ubicación y pernocte</h3>
      <p style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <select
          value={planillaId}
          disabled={ocupado}
          onChange={(e) => onPlanilla(e.target.value)}
          aria-label="Planilla de la ubicación y el pernocte"
        >
          {planillas.map((p) => (
            <option key={p.id} value={p.id}>{p.nombre}</option>
          ))}
        </select>
        <button
          type="button"
          className="tk-boton tk-boton--fuerte"
          disabled={!planillaId || ocupado}
          onClick={actualizar}
        >
          {ocupado ? "Actualizando…" : "Actualizar ubicación y pernocte"}
        </button>
      </p>

      {error && (
        <div className="panel-aviso suave">
          <p>{error}</p>
        </div>
      )}

      {lineas.length > 0 && (
        <div className="panel-aviso suave">
          {lineas.map((l) => (
            <p key={l}>{l}</p>
          ))}
        </div>
      )}

      {reemplazos && (
        <div className="panel-aviso suave">
          {reemplazos.titulo && <p>{reemplazos.titulo}</p>}
          {reemplazos.lineas.length > 0 && (
            <ul>
              {reemplazos.lineas.map((l) => (
                <li key={l}>{l}</li>
              ))}
            </ul>
          )}
          {reemplazos.plegado && (
            <details>
              <summary>{reemplazos.plegado.resumen}</summary>
              <ul>
                {reemplazos.plegado.lineas.map((l) => (
                  <li key={l}>{l}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {detalle && detalle.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table className="tabla fija">
            <thead>
              <tr>
                <th>TRA</th>
                <th>Ubicación actualizada</th>
                <th>Pernocte</th>
              </tr>
            </thead>
            <tbody>
              {detalle.map((u) => (
                <tr key={u.clave}>
                  <td>{u.clave}</td>
                  <td>
                    {u.ubicacion_actual ? (
                      <>
                        {u.ubicacion_actual}
                        {u.cuando && <span className="tk-meta"> · {horaAr(u.cuando)}</span>}
                      </>
                    ) : (
                      <span className="tk-meta">{u.motivo_ubicacion || "Sin datos."}</span>
                    )}
                  </td>
                  <td>
                    {u.pernocte?.texto || <span className="tk-meta">{u.motivo_pernocte || "Sin datos."}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
